// Categorical colour for the issuing charts.
//
// The chart primitives are generic — they know a member by its id and ask a
// resolver what colour it draws in. This module is where the domain meaning
// lives: the item-type tokens the rest of the app already uses for its type
// pills, so a medication is the same blue on the trend chart as it is on a stock
// row.
//
// The `-fg` half of each token pair, not the `-bg` half: the `-bg` tints are
// near-white in the light theme (`oklch(0.95 …)`) and would make consumable and
// "other" indistinguishable at bar size. The `-fg` values are saturated in both
// themes, and every value here is a CSS variable, so a `.dark` toggle recolours
// the charts without the components knowing there is a theme at all.
//
// Item-type colour is spread across three files, and a sixth item type is a
// change to all three:
//   1. `src/styles/globals.css`      — declares `--t-<type>-bg/-fg`, light + dark.
//   2. `tailwind.config.ts`          — maps those variables to utility classes.
//   3. `components/ui/badge.tsx`     — picks the utility class for the type pill.
// This file is the fourth reader of the same tokens; `chartTones.test.ts` is
// what catches a token here that the stylesheet never declared.

import type { ItemType } from '@/lib/itemTypeConfig';

/** Resolves a series member id to the CSS colour its bar or swatch is drawn in. */
export type ToneResolver = (memberId: string) => string;

/**
 * The tone for a member no map knows. A sixth item type is expected to arrive
 * one day, and it must render as a plain grey band rather than as an untoned
 * (invisible) segment or a crash. Deliberately NOT the "other" tone: an unknown
 * type is not the `other` type, and colouring it as one would quietly merge two
 * different things in the same chart.
 */
export const FALLBACK_TONE = 'var(--muted-2)';

const TONE_BY_ITEM_TYPE: Record<ItemType, string> = {
  medication: 'var(--t-medication-fg)',
  consumable: 'var(--t-consumable-fg)',
  medical_device: 'var(--t-medical-device-fg)',
  reagent: 'var(--t-reagent-fg)',
  other: 'var(--t-other-fg)',
};

// A Map rather than direct indexing: the wire hands us member ids as plain
// strings, and a `Record<ItemType, …>` lookup would type an unknown id's result
// as `string`, making the fallback below look like dead code to the reader.
const TONES = new Map<string, string>(Object.entries(TONE_BY_ITEM_TYPE));

export const itemTypeTone: ToneResolver = (memberId) => TONES.get(memberId) ?? FALLBACK_TONE;
